import { FolderPlus, Folders } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCollections, useCollectionsWithArchived } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { CURRENT_WORKSPACE, usePolicy } from "~/policies/usePolicy";
import { useShellStore } from "~/shell/store";

/**
 * W10 §8 — the Archived view is a FILTER on the same list, not a second endpoint: the platform's
 * `statusFilter: ["archived"]` includes archived collections alongside the live ones, so the
 * split happens here on `archivedAt`.
 */
export function CollectionsScene() {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const live = useCollections();
  const withArchived = useCollectionsWithArchived();
  const source = showArchived ? withArchived : live;
  const isLoading = source.isLoading;
  const collections = showArchived
    ? (withArchived.data ?? []).filter((collection) => collection.archivedAt !== null)
    : live.data;
  const can = usePolicy(CURRENT_WORKSPACE);
  const openDialog = useShellStore((state) => state.openDialog);
  useDocumentTitle(t("Collections"));

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <Text as="h1" variant="h2">
          {t("Collections")}
        </Text>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setShowArchived((current) => !current)}>
            {showArchived ? t("Show active") : t("Show archived")}
          </Button>
          {can[ABILITY.workspaceCreateCollection] ? (
            <Button
              variant="primary"
              icon={FolderPlus}
              onClick={() => openDialog({ kind: "newCollection" })}
            >
              {t("New collection")}
            </Button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={t("Loading")} />
        </div>
      ) : !collections?.length ? (
        <Empty
          icon={Folders}
          message={showArchived ? t("Nothing is archived") : t("No collections yet")}
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {collections.map((collection) => (
            <li key={collection.id}>
              <Link
                to="/collections/$collectionId"
                params={{ collectionId: collection.id }}
                className="flex h-full flex-col gap-1 rounded-md border border-divider p-4 hover:bg-hover-bg"
              >
                <span className="text-h3">{collection.emoji ?? "📁"}</span>
                <span className="text-body font-medium text-text">{collection.name}</span>
                {collection.description ? (
                  <span className="text-small text-text-secondary">{collection.description}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
